import React, { useEffect, useState, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import MaterialTable from '../components/MaterialTableAdapter'
import {
    getAffiliateStats,
    getAffiliateReferrals,
    getAffiliateEarnings
} from '../actions/userActions'

// Icons
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import ScreenHeader from '../components/layout/ScreenHeader'

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const AffiliateDashboard = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // State
    const [activeTab, setActiveTab] = useState('referrals') // 'referrals' or 'earnings'

    // Redux State
    const userLogin = useSelector((state) => state.userLogin)
    const { userInfo } = userLogin

    const getAffiliateStatsState = useSelector((state) => state.getAffiliateStats)
    const { loading: loadingStats, stats } = getAffiliateStatsState || {}

    const getAffiliateReferralsState = useSelector((state) => state.getAffiliateReferrals)
    const { loading: loadingReferrals, referralsData } = getAffiliateReferralsState || {}

    const getAffiliateEarningsState = useSelector((state) => state.getAffiliateEarnings)
    const { loading: loadingEarnings, earningsData } = getAffiliateEarningsState || {}

    // Check Permissions
    useEffect(() => {
        if (!userInfo) {
            navigate('/')
        }
    }, [userInfo, navigate])

    // Initial Fetch
    useEffect(() => {
        dispatch(getAffiliateStats())
        dispatch(getAffiliateReferrals())
        if (activeTab === 'earnings') {
            dispatch(getAffiliateEarnings())
        }
    }, [dispatch, activeTab])

    // Table Columns
    const referralColumns = [
        { title: 'Name', field: 'name' },
        { title: 'Email', field: 'email' },
        { title: 'Phone', field: 'phoneNumber' },
        {
            title: 'Commission Split',
            field: 'commissionSplit',
            render: rowData => (
                <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-800">
                    {rowData.commissionSplit || 0}%
                </span>
            )
        },
        {
            title: 'Status',
            field: 'verification.isFullyVerified',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${rowData.verification?.isFullyVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {rowData.verification?.isFullyVerified ? 'Verified' : 'Pending'}
                </span>
            )
        },
        {
            title: 'Assigned On',
            field: 'assignedAt',
            render: rowData => new Date(rowData.assignedAt || rowData.createdAt).toLocaleDateString()
        }
    ]

    const earningsColumns = [
        {
            title: 'Date',
            field: 'commissionDate',
            render: rowData => new Date(rowData.commissionDate).toLocaleDateString('en-IN')
        },
        {
            title: 'Amount',
            field: 'amount',
            render: rowData => `₹${rowData.amount?.toFixed(2)}`
        },
        {
            title: 'Type',
            field: 'type',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${
                    rowData.type === 'referral' ? 'bg-blue-100 text-blue-800' :
                    rowData.type === 'bonus' ? 'bg-purple-100 text-purple-800' :
                    rowData.type === 'session_hours' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-gray-100 text-gray-800'
                }`}>
                    {rowData.type?.replace('_', ' ').toUpperCase()}
                </span>
            )
        },
        {
            title: 'Status',
            field: 'status',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${
                    rowData.status === 'paid' ? 'bg-green-100 text-green-800' :
                    rowData.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                    rowData.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                }`}>
                    {rowData.status?.toUpperCase() || 'PENDING'}
                </span>
            )
        },
        {
            title: 'Remarks',
            field: 'remarks',
            render: rowData => rowData.remarks || '-'
        }
    ]

    return (
        <div className='w-full h-full'>
            <ScreenHeader title="Affiliate dashboard" />

            {/* Stats Cards */}
            <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
                <div className='bg-white p-6 rounded-lg shadow-md border-l-4 border-emerald-500'>
                    <h3 className='text-gray-500 text-sm font-medium uppercase'>Total Referrals</h3>
                    <p className='text-3xl font-bold text-gray-800 mt-2'>{stats?.totalReferrals || 0}</p>
                </div>
                <div className='bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500'>
                    <h3 className='text-gray-500 text-sm font-medium uppercase'>Total Earnings</h3>
                    <p className='text-3xl font-bold text-gray-800 mt-2'>₹{stats?.totalEarnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div className='bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500'>
                    <h3 className='text-gray-500 text-sm font-medium uppercase'>Pending Payout</h3>
                    <p className='text-3xl font-bold text-gray-800 mt-2'>₹{stats?.pendingEarnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div className='bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500'>
                    <h3 className='text-gray-500 text-sm font-medium uppercase'>Paid</h3>
                    <p className='text-3xl font-bold text-gray-800 mt-2'>₹{stats?.paidEarnings?.toFixed(2) || '0.00'}</p>
                </div>
            </div>

            {/* Tabs & Table */}
            <div className='bg-white shadow-md rounded-lg overflow-hidden'>
                <div className='flex border-b border-gray-200'>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'referrals'
                                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('referrals')}
                    >
                        My Referrals
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'earnings'
                                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('earnings')}
                    >
                        Earnings History
                    </button>
                </div>

                <div className='p-0'>
                    {activeTab === 'referrals' && (
                        <MaterialTable
                            title=""
                            icons={tableIcons}
                            columns={referralColumns}
                            data={referralsData?.referrals || []}
                            isLoading={loadingReferrals}
                            options={{
                                search: true,
                                paging: true,
                                pageSize: 10,
                                headerStyle: { fontWeight: 'bold' },
                                emptyRowsWhenPaging: false
                            }}
                        />
                    )}

                    {activeTab === 'earnings' && (
                        <MaterialTable
                            title=""
                            icons={tableIcons}
                            columns={earningsColumns}
                            data={earningsData?.earnings || []}
                            isLoading={loadingEarnings}
                            options={{
                                search: true,
                                paging: true,
                                pageSize: 10,
                                headerStyle: { fontWeight: 'bold' },
                                emptyRowsWhenPaging: false
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default AffiliateDashboard
